import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import type { Maybe } from '@graphql-tools/utils';
import { DateTime } from 'luxon';
import { NotificationSeenEventlog } from '../eventlogs/NotificationSeenEventlog.js';

/**
 * @entity ChancenNotification
 *
 */
@Entity()
export class ChancenNotification extends MongoEntity {
  public static override _class = 'ChancenNotification';
  public static override _collectionName = 'notifications';

  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  public createdAt: Maybe<DateTime>;

  @Field(type => ID)
  public createdByID: Maybe<string>;

  @Field(type => String, {
    resolve: function (this: ChancenNotification) {
      return async () => {
        // TODO Kai we need a raw dataloader with uses rawCollection, or we need a better publish filter for this stuff
        if (!this.createdByID) return '';
        const coll = await getRawCollection<EntityData<ChancenwerkUser>>(ChancenwerkUser._collectionName);
        const creator = await coll.findOne({ _id: this.createdByID });

        if (!creator) return '';
        const conUser = constructValue(creator) as ChancenwerkUser;
        return conUser.toString();
      };
    },
  })
  public creatorName: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  public title: Maybe<string>;

  @Field(type => String)
  public content: Maybe<string>;

  /**
   * Creates an eventlog, that a user has seen a specific notification. This method checks, if it would create a duplicate. In this case it silently return the duplicate _id, so no error handling is required
   * @param userID The user which has seen the notification
   * @param notificationID The Notification which has been seen
   * @returns the _id of the eventlog
   */
  @Mutation(type => String)
  public static setSeenForNotification(
    @Arg('userID', type => String) userID: string,
    @Arg('notificationID', type => String) notificationID: string,
  ) {
    return async (gqlFields: void) => {
      const userCollection = await getCollection(AdornisUser._class);
      const user = await userCollection.findOne({
        _id: userID,
      });
      if (!user) throw new Error('Invalid userId');

      const notificationCollection = await getCollection(this._class);
      const notification = await notificationCollection.findOne({
        _id: notificationID,
      });
      if (!notification) throw new Error('Invalid notificationID');

      let idOfNotification = '';

      const notificationEventlogCollection = await getCollection(NotificationSeenEventlog._class);
      const duplicate = await notificationEventlogCollection.findOne({
        userID: user._id,
        notificationID: notification._id,
      });

      if (duplicate) {
        return duplicate._id;
      }

      idOfNotification = await new NotificationSeenEventlog({
        userID,
        notificationID,
      }).create();

      return idOfNotification;
    };
  }

  @Mutation(type => Boolean)
  public static getSeenForNotification(
    @Arg('userID', type => String) userID: string,
    @Arg('notificationID', type => String) notificationID: string,
  ) {
    return async (gqlFields: void) => {
      const userCollection = await getCollection(AdornisUser._class);
      const user = await userCollection.findOne({
        _id: userID,
      });

      if (!user) throw new Error('Invalid userID: ' + userID);

      const notificationCollection = await getCollection(this._class);
      const notification = await notificationCollection.findOne({
        _id: notificationID,
      });
      if (!notification) throw new Error('Invalid notificationID: ' + notificationID);

      const notificationEventlogCollection = await getCollection(NotificationSeenEventlog._class);
      const eventlog = await notificationEventlogCollection.findOne({
        userID: user._id,
        notificationID: notification._id,
      });
      if (eventlog) return true;
      return false;
    };
  }
}
